import { LoadingSpinner } from "@/shared/components/feedback/LoadingSpinner";
import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { SearchableSelect } from "@/shared/components/ui/searchable-select";
import { useOrganizationOnboarding } from "../hooks/useOrganizationOnboarding";
import type { OrganizationCandidate, StartOnboardingDto } from "../types/onboarding-types";
import { useCurrentPageAccess } from "@/app/router";

export function OrganizationOnboardingPage() {
  const vm = useOrganizationOnboarding();
  const { canWriteCurrentPage } = useCurrentPageAccess();
  return (
    <div className="space-y-6">
      <Breadcrumbs />
      <PageHeader
        title="Onboarding"
        description="새로운 사일로 조직을 프리즘에 온보딩 합니다. 조직 전용 RabbitMQ 큐 생성 및 바인딩을 수행합니다."
      />

      {vm.step === "intro" && (
        <Card className="p-6 space-y-4">
          <div className="whitespace-pre-wrap text-sm text-muted-foreground">
            {`새로운 사일로 조직을 프리즘에 온보딩 합니다.

- 조직 전용 RabbitMQ 큐 생성 및 바인딩 추가
- 공용(pool) 큐 매핑은 gatekeeper가 자동으로 처리하므로 별도 온보딩이 필요하지 않습니다.`}
          </div>

          <Button onClick={() => vm.setStep("user_input")} disabled={!canWriteCurrentPage}>
            시작하기
          </Button>
        </Card>
      )}

      {vm.step === "user_input" && (
        <Card className="p-6 space-y-6">
          <div className="space-y-2">
            <div className="text-sm font-medium">온보딩 할 조직</div>
            <div className="text-sm text-muted-foreground">
              엑스퍼트에 등록된 조직 중 아직 온보딩 되지 않은 조직만 표시됩니다.
            </div>
            <SearchableSelect
              className="w-[320px]"
              value={vm.form.new_org_name}
              onValueChange={(v) =>
                vm.setForm((p: StartOnboardingDto) => ({ ...p, new_org_name: v }))
              }
              options={vm.orgCandidates.map((o: OrganizationCandidate) => ({
                value: o.eng_identifier,
                label: o.eng_identifier,
              }))}
              placeholder="조직을 선택하세요"
            />
          </div>

          <div className="flex gap-2">
            <Button
              onClick={async () => {
                const res = await vm.start();
                if (!res.ok) {
                  alert(res.error);
                }
              }}
            >
              온보딩 실행
            </Button>
            <Button variant="outline" onClick={() => vm.setStep("intro")}>
              취소
            </Button>
          </div>
        </Card>
      )}

      {vm.step === "onboarding" && (
        <Card className="p-6 flex items-center gap-3">
          <LoadingSpinner size="sm" />
          <span className="text-sm font-medium">온보딩을 진행하고 있습니다.</span>
        </Card>
      )}

      {vm.step === "done" && (
        <Card className="p-6 space-y-4">
          {vm.error ? (
            <div className="text-sm font-medium text-destructive">온보딩에 실패했습니다: {vm.error}</div>
          ) : (
            <div className="text-sm font-medium text-emerald-600">온보딩이 완료되었습니다.</div>
          )}
          <Button onClick={vm.finish}>확인</Button>
        </Card>
      )}
    </div>
  );
}
